import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Wrench, Plus, Loader2, Calendar, AlertCircle, CheckCircle2,
  XCircle, Clock, FileText, Paperclip, Download, Trash2, Play,
  CheckSquare, User as UserIcon, AlertTriangle, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  aberta: { label: 'Aberta', color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
  factivel: { label: 'Factível', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  em_andamento: { label: 'Em Andamento', color: 'bg-blue-100 text-blue-700', icon: Clock },
  concluida: { label: 'Concluída', color: 'bg-green-100 text-green-700', icon: CheckSquare },
  nao_factivel: { label: 'Não Factível', color: 'bg-red-100 text-red-700', icon: XCircle },
  cancelada: { label: 'Cancelada', color: 'bg-slate-200 text-slate-600', icon: X },
};

const PRIORIDADE_CONFIG = {
  baixa: { label: 'Baixa', color: 'bg-slate-100 text-slate-600' },
  media: { label: 'Média', color: 'bg-blue-100 text-blue-700' },
  alta: { label: 'Alta', color: 'bg-orange-100 text-orange-700' },
  critica: { label: 'Crítica', color: 'bg-red-100 text-red-700' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.aberta;
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.color} flex items-center gap-1 border-0`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </Badge>
  );
};

const PrioridadeBadge = ({ prioridade }) => {
  const cfg = PRIORIDADE_CONFIG[prioridade] || PRIORIDADE_CONFIG.media;
  return <Badge className={`${cfg.color} border-0`}>{cfg.label}</Badge>;
};

const formatDate = (str) => {
  if (!str) return '-';
  try {
    return new Date(str.length === 10 ? str + 'T00:00:00' : str).toLocaleDateString('pt-BR');
  } catch { return str; }
};

const formatDateTime = (str) => {
  if (!str) return '-';
  try {
    return new Date(str).toLocaleString('pt-BR');
  } catch { return str; }
};

export default function SCP() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [scps, setScps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('todas');

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [newScp, setNewScp] = useState({
    titulo: '', descricao: '', prioridade: 'media', data_desejada: ''
  });
  const [pendingFiles, setPendingFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  // Detail modal
  const [showDetail, setShowDetail] = useState(false);
  const [selected, setSelected] = useState(null);

  // Avaliar modal
  const [showAvaliar, setShowAvaliar] = useState(false);
  const [avaliarForm, setAvaliarForm] = useState({
    factivel: true, justificativa: '', data_prevista: ''
  });

  // Cancelar / Excluir
  const [showCancelar, setShowCancelar] = useState(false);
  const [showExcluir, setShowExcluir] = useState(false);

  // Conclude
  const [showConcluir, setShowConcluir] = useState(false);
  const [observacaoConclusao, setObservacaoConclusao] = useState('');

  const isAdminOrEng = user?.role === 'admin' || user?.role === 'engenharia';

  const fetchDashboard = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/scp/dashboard`);
      setDashboard(r.data);
    } catch { /* noop */ }
  }, []);

  const fetchList = useCallback(async (tab) => {
    setLoading(true);
    try {
      const params = {};
      if (tab === 'minhas') params.minhas = true;
      else if (tab === 'avaliar') params.status = 'aberta';
      else if (tab === 'em_execucao') params.status = 'em_andamento';
      const r = await axios.get(`${API_URL}/api/scp`, { params });
      setScps(r.data);
    } catch (err) {
      toast.error('Erro ao carregar SCPs');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchList(activeTab);
  }, [activeTab, fetchDashboard, fetchList]);

  const handleCreate = async () => {
    if (!newScp.titulo || newScp.titulo.length < 3) return toast.error('Título obrigatório (mín. 3 caracteres)');
    if (!newScp.descricao || newScp.descricao.length < 10) return toast.error('Descrição obrigatória (mín. 10 caracteres)');
    setSaving(true);
    try {
      const payload = { ...newScp, data_desejada: newScp.data_desejada || null };
      const r = await axios.post(`${API_URL}/api/scp`, payload);
      const created = r.data;
      // Upload pending files
      for (const f of pendingFiles) {
        const fd = new FormData();
        fd.append('file', f);
        await axios.post(`${API_URL}/api/scp/${created.id}/anexos`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success(`${created.codigo} criada com sucesso!`);
      setShowCreate(false);
      setNewScp({ titulo: '', descricao: '', prioridade: 'media', data_desejada: '' });
      setPendingFiles([]);
      fetchDashboard();
      fetchList(activeTab);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar SCP');
    } finally { setSaving(false); }
  };

  const openDetail = async (scp) => {
    try {
      const r = await axios.get(`${API_URL}/api/scp/${scp.id}`);
      setSelected(r.data);
      setShowDetail(true);
    } catch {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const refreshSelected = async () => {
    if (!selected) return;
    const r = await axios.get(`${API_URL}/api/scp/${selected.id}`);
    setSelected(r.data);
    fetchList(activeTab);
    fetchDashboard();
  };

  const handleAvaliar = async () => {
    if (avaliarForm.justificativa.trim().length < 5) return toast.error('Justificativa obrigatória (mín. 5 caracteres)');
    if (avaliarForm.factivel && !avaliarForm.data_prevista) return toast.error('Informe a data prevista');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/scp/${selected.id}/avaliar`, avaliarForm);
      toast.success(avaliarForm.factivel ? 'SCP marcada como FACTÍVEL' : 'SCP marcada como NÃO FACTÍVEL');
      setShowAvaliar(false);
      setAvaliarForm({ factivel: true, justificativa: '', data_prevista: '' });
      refreshSelected();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao avaliar');
    } finally { setSaving(false); }
  };

  const handleIniciar = async () => {
    try {
      await axios.post(`${API_URL}/api/scp/${selected.id}/iniciar`);
      toast.success('SCP em andamento');
      refreshSelected();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    }
  };

  const handleConcluir = async () => {
    setSaving(true);
    try {
      const params = observacaoConclusao ? { observacao: observacaoConclusao } : {};
      await axios.post(`${API_URL}/api/scp/${selected.id}/concluir`, null, { params });
      toast.success('SCP concluída');
      setShowConcluir(false);
      setObservacaoConclusao('');
      refreshSelected();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    } finally { setSaving(false); }
  };

  const handleCancelar = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/scp/${selected.id}/cancelar`);
      toast.success('SCP cancelada');
      setShowCancelar(false);
      refreshSelected();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    } finally { setSaving(false); }
  };

  const handleExcluir = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API_URL}/api/scp/${selected.id}`);
      toast.success('SCP excluída');
      setShowExcluir(false);
      setShowDetail(false);
      fetchList(activeTab);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    } finally { setSaving(false); }
  };

  const handleUploadAnexo = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      try {
        await axios.post(`${API_URL}/api/scp/${selected.id}/anexos`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch (err) {
        toast.error(`Erro ao enviar ${f.name}`);
      }
    }
    toast.success('Anexo(s) enviado(s)');
    e.target.value = '';
    refreshSelected();
  };

  const handleDownloadAnexo = async (anexo) => {
    try {
      const r = await axios.get(
        `${API_URL}/api/scp/${selected.id}/anexos/${anexo.id}/download`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = anexo.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erro no download');
    }
  };

  const handleRemoveAnexo = async (anexo) => {
    if (!window.confirm(`Excluir o anexo ${anexo.filename}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/scp/${selected.id}/anexos/${anexo.id}`);
      toast.success('Anexo removido');
      refreshSelected();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="scp-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Wrench className="w-7 h-7 text-blue-700" />
              SCP — Solicitação de Correção de Projeto
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Solicite correções e alterações em projetos. A engenharia avalia a viabilidade e informa prazo.
            </p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="btn-nova-scp">
            <Plus className="w-4 h-4 mr-2" />
            Nova SCP
          </Button>
        </div>

        {/* Stats */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <StatCard label="Total" value={dashboard.total} color="text-slate-700" />
            <StatCard label="Abertas" value={dashboard.abertas} color="text-amber-700" />
            <StatCard label="Factíveis" value={dashboard.factiveis} color="text-emerald-700" />
            <StatCard label="Em Andamento" value={dashboard.em_andamento} color="text-blue-700" />
            <StatCard label="Concluídas" value={dashboard.concluidas} color="text-green-700" />
            <StatCard label="Não Factíveis" value={dashboard.nao_factiveis} color="text-red-700" />
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="todas" data-testid="tab-todas">Todas</TabsTrigger>
            <TabsTrigger value="minhas" data-testid="tab-minhas">Minhas Solicitações</TabsTrigger>
            {isAdminOrEng && (
              <TabsTrigger value="avaliar" data-testid="tab-avaliar">
                Para Avaliar {dashboard?.abertas > 0 && (
                  <Badge className="ml-2 bg-amber-100 text-amber-700">{dashboard.abertas}</Badge>
                )}
              </TabsTrigger>
            )}
            <TabsTrigger value="em_execucao" data-testid="tab-em_execucao">Em Execução</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="space-y-3 mt-4">
            {loading ? (
              <div className="py-12 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-400" /></div>
            ) : scps.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Wrench className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Nenhuma SCP encontrada</p>
                </CardContent>
              </Card>
            ) : (
              scps.map(scp => (
                <Card
                  key={scp.id}
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => openDetail(scp)}
                  data-testid={`scp-card-${scp.codigo}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm font-semibold text-blue-700">{scp.codigo}</span>
                          <StatusBadge status={scp.status} />
                          <PrioridadeBadge prioridade={scp.prioridade} />
                          {scp.anexos_count > 0 && (
                            <Badge variant="outline" className="flex items-center gap-1">
                              <Paperclip className="w-3 h-3" />
                              {scp.anexos_count}
                            </Badge>
                          )}
                        </div>
                        <p className="font-medium mt-2 text-slate-900">{scp.titulo}</p>
                        <p className="text-sm text-slate-500 mt-1 line-clamp-2">{scp.descricao}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 flex-wrap">
                          <span className="flex items-center gap-1">
                            <UserIcon className="w-3 h-3" />
                            {scp.solicitante_nome}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Criada em {formatDate(scp.created_at)}
                          </span>
                          {scp.data_desejada && (
                            <span className="flex items-center gap-1 text-amber-600">
                              <Clock className="w-3 h-3" />
                              Desejada: {formatDate(scp.data_desejada)}
                            </span>
                          )}
                          {scp.data_prevista && (
                            <span className="flex items-center gap-1 text-blue-600 font-medium">
                              <CheckCircle2 className="w-3 h-3" />
                              Prevista: {formatDate(scp.data_prevista)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>

        {/* === Create Modal === */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Nova Solicitação de Correção</DialogTitle>
              <DialogDescription>Descreva detalhadamente o que precisa ser corrigido. A engenharia avaliará a viabilidade.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Título *</Label>
                <Input
                  value={newScp.titulo}
                  onChange={(e) => setNewScp({ ...newScp, titulo: e.target.value })}
                  placeholder="Ex: Corrigir cota lateral do desenho 8-010-60137"
                  data-testid="input-titulo"
                />
              </div>
              <div className="space-y-2">
                <Label>Descrição detalhada *</Label>
                <Textarea
                  value={newScp.descricao}
                  onChange={(e) => setNewScp({ ...newScp, descricao: e.target.value })}
                  placeholder="Descreva exatamente o que precisa ser alterado..."
                  rows={5}
                  data-testid="input-descricao"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prioridade</Label>
                  <Select value={newScp.prioridade} onValueChange={(v) => setNewScp({ ...newScp, prioridade: v })}>
                    <SelectTrigger data-testid="select-prioridade"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="baixa">Baixa</SelectItem>
                      <SelectItem value="media">Média</SelectItem>
                      <SelectItem value="alta">Alta</SelectItem>
                      <SelectItem value="critica">Crítica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Data desejada (opcional)</Label>
                  <Input
                    type="date"
                    value={newScp.data_desejada}
                    onChange={(e) => setNewScp({ ...newScp, data_desejada: e.target.value })}
                    data-testid="input-data-desejada"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Anexos (opcional)</Label>
                <Input
                  type="file"
                  multiple
                  onChange={(e) => setPendingFiles(Array.from(e.target.files))}
                  data-testid="input-anexos"
                />
                {pendingFiles.length > 0 && (
                  <p className="text-xs text-slate-500">{pendingFiles.length} arquivo(s) selecionado(s)</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
              <Button onClick={handleCreate} disabled={saving} data-testid="btn-criar-scp">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Criar SCP
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* === Detail Modal === */}
        <Dialog open={showDetail} onOpenChange={setShowDetail}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            {selected && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-3 flex-wrap">
                    <DialogTitle className="font-mono">{selected.codigo}</DialogTitle>
                    <StatusBadge status={selected.status} />
                    <PrioridadeBadge prioridade={selected.prioridade} />
                  </div>
                  <DialogDescription className="text-base font-medium text-slate-800 mt-2">
                    {selected.titulo}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 p-3 rounded-lg">
                    <div><span className="text-slate-500">Solicitante:</span> <strong>{selected.solicitante_nome}</strong></div>
                    <div><span className="text-slate-500">Criada:</span> {formatDateTime(selected.created_at)}</div>
                    {selected.data_desejada && (
                      <div><span className="text-slate-500">Data desejada:</span> {formatDate(selected.data_desejada)}</div>
                    )}
                    {selected.data_prevista && (
                      <div className="text-blue-700"><span className="text-slate-500">Data prevista:</span> <strong>{formatDate(selected.data_prevista)}</strong></div>
                    )}
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">DESCRIÇÃO</Label>
                    <div className="p-3 border rounded-lg bg-white whitespace-pre-wrap text-sm">{selected.descricao}</div>
                  </div>

                  {selected.avaliada_em && (
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">AVALIAÇÃO DA ENGENHARIA</Label>
                      <div className={`p-3 border-l-4 rounded-lg ${selected.factivel ? 'bg-emerald-50 border-emerald-500' : 'bg-red-50 border-red-500'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          {selected.factivel ? (
                            <Badge className="bg-emerald-100 text-emerald-700 border-0">FACTÍVEL</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700 border-0">NÃO FACTÍVEL</Badge>
                          )}
                          <span className="text-xs text-slate-600">por {selected.avaliada_por_nome} em {formatDateTime(selected.avaliada_em)}</span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{selected.justificativa}</p>
                        {selected.factivel && selected.data_prevista && (
                          <p className="text-sm font-medium mt-2 text-emerald-800">
                            📅 Data prevista de execução: {formatDate(selected.data_prevista)}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {selected.concluida_em && (
                    <div className="p-3 bg-green-50 border-l-4 border-green-500 rounded-lg text-sm">
                      <strong className="text-green-800">✅ Concluída</strong> em {formatDateTime(selected.concluida_em)} por {selected.concluida_por_nome}
                      {selected.observacao_conclusao && <p className="mt-1 italic">"{selected.observacao_conclusao}"</p>}
                    </div>
                  )}

                  {/* Anexos */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-slate-500">ANEXOS ({selected.anexos?.length || 0})</Label>
                      <label className="text-xs text-blue-600 cursor-pointer hover:underline flex items-center gap-1">
                        <Paperclip className="w-3 h-3" />
                        Adicionar anexo
                        <input type="file" multiple className="hidden" onChange={handleUploadAnexo} data-testid="input-upload-anexo" />
                      </label>
                    </div>
                    {selected.anexos?.length > 0 ? (
                      <div className="space-y-1">
                        {selected.anexos.map(a => (
                          <div key={a.id} className="flex items-center justify-between p-2 border rounded-lg bg-white text-sm">
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                              <span className="truncate">{a.filename}</span>
                              <span className="text-xs text-slate-400">({(a.file_size / 1024).toFixed(1)} KB)</span>
                            </div>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => handleDownloadAnexo(a)}>
                                <Download className="w-3 h-3" />
                              </Button>
                              {(user?.role === 'admin' || user?.role === 'engenharia' || a.uploaded_by === user?.id) && (
                                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleRemoveAnexo(a)}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic">Nenhum anexo</p>
                    )}
                  </div>
                </div>

                <DialogFooter className="gap-2 flex-wrap">
                  {selected.pode_avaliar && (
                    <Button onClick={() => setShowAvaliar(true)} data-testid="btn-avaliar">
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Avaliar
                    </Button>
                  )}
                  {selected.pode_iniciar && (
                    <Button onClick={handleIniciar} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-iniciar">
                      <Play className="w-4 h-4 mr-1" />
                      Iniciar Execução
                    </Button>
                  )}
                  {selected.pode_concluir && (
                    <Button onClick={() => setShowConcluir(true)} className="bg-green-600 hover:bg-green-700" data-testid="btn-concluir">
                      <CheckSquare className="w-4 h-4 mr-1" />
                      Concluir
                    </Button>
                  )}
                  {selected.pode_cancelar && (
                    <Button variant="outline" className="text-amber-700" onClick={() => setShowCancelar(true)}>
                      Cancelar SCP
                    </Button>
                  )}
                  {user?.role === 'admin' && (
                    <Button variant="outline" className="text-red-600" onClick={() => setShowExcluir(true)}>
                      <Trash2 className="w-4 h-4 mr-1" />
                      Excluir
                    </Button>
                  )}
                  <Button variant="ghost" onClick={() => setShowDetail(false)}>Fechar</Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* === Avaliar Modal === */}
        <Dialog open={showAvaliar} onOpenChange={setShowAvaliar}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Avaliação da Engenharia</DialogTitle>
              <DialogDescription>Indique se a correção é factível e a justificativa técnica.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Decisão *</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={avaliarForm.factivel ? 'default' : 'outline'}
                    className={avaliarForm.factivel ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                    onClick={() => setAvaliarForm({ ...avaliarForm, factivel: true })}
                    data-testid="btn-decisao-factivel"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Factível
                  </Button>
                  <Button
                    type="button"
                    variant={!avaliarForm.factivel ? 'default' : 'outline'}
                    className={!avaliarForm.factivel ? 'bg-red-600 hover:bg-red-700' : ''}
                    onClick={() => setAvaliarForm({ ...avaliarForm, factivel: false })}
                    data-testid="btn-decisao-nao-factivel"
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Não Factível
                  </Button>
                </div>
              </div>
              {avaliarForm.factivel && (
                <div className="space-y-2">
                  <Label>Data prevista de execução *</Label>
                  <Input
                    type="date"
                    value={avaliarForm.data_prevista}
                    onChange={(e) => setAvaliarForm({ ...avaliarForm, data_prevista: e.target.value })}
                    data-testid="input-data-prevista"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label>Justificativa *</Label>
                <Textarea
                  value={avaliarForm.justificativa}
                  onChange={(e) => setAvaliarForm({ ...avaliarForm, justificativa: e.target.value })}
                  placeholder={avaliarForm.factivel
                    ? 'Como será executada a correção, recursos necessários, etc.'
                    : 'Explique por que não é factível (técnico, prazo, recursos, etc.)'}
                  rows={4}
                  data-testid="input-justificativa"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAvaliar(false)}>Cancelar</Button>
              <Button onClick={handleAvaliar} disabled={saving} data-testid="btn-confirmar-avaliacao">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Registrar Avaliação
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* === Concluir Modal === */}
        <Dialog open={showConcluir} onOpenChange={setShowConcluir}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Concluir SCP</DialogTitle>
              <DialogDescription>Adicione uma observação final sobre a execução (opcional).</DialogDescription>
            </DialogHeader>
            <Textarea
              value={observacaoConclusao}
              onChange={(e) => setObservacaoConclusao(e.target.value)}
              placeholder="Ex: Correção aplicada, desenho atualizado para revisão Z1."
              rows={4}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConcluir(false)}>Cancelar</Button>
              <Button onClick={handleConcluir} disabled={saving} className="bg-green-600 hover:bg-green-700">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Marcar como Concluída
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* === Cancelar AlertDialog === */}
        <AlertDialog open={showCancelar} onOpenChange={setShowCancelar}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancelar SCP?</AlertDialogTitle>
              <AlertDialogDescription>
                A SCP será marcada como cancelada e não poderá mais ser avaliada.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Voltar</AlertDialogCancel>
              <AlertDialogAction onClick={handleCancelar} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
                Sim, cancelar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* === Excluir AlertDialog === */}
        <AlertDialog open={showExcluir} onOpenChange={setShowExcluir}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Excluir SCP permanentemente?
              </AlertDialogTitle>
              <AlertDialogDescription>
                Esta ação é irreversível. Todos os anexos também serão removidos.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleExcluir} disabled={saving} className="bg-red-600 hover:bg-red-700">
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}

const StatCard = ({ label, value, color }) => (
  <Card>
    <CardContent className="p-3">
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </CardContent>
  </Card>
);
